import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { User, TelegramUser } from '@/types';
import { translateAuthError } from '@/lib/auth-error-messages';
import {
  initTelegramWebApp,
  getTelegramUser,
  getTelegramThemeParams,
  getTelegramColorScheme,
  applyTelegramTheme,
  TelegramThemeParams,
  hasTelegramInitData,
  isTelegramApiAvailable
} from '@/integrations/telegram/telegramService';
import { useOpenPanelStore } from '@/stores/openPanelStore';
import { getStoredAttributionKey } from '@/lib/attribution';

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';
export type AuthContext = 'web' | 'telegram';

interface AuthState {
  // User state
  user: User | null;
  status: AuthStatus;
  authContext: AuthContext;

  // Telegram specific state
  isTelegram: boolean;
  telegramUser: TelegramUser | null;
  isInitialized: boolean;
  isAuthenticating: boolean;

  // Theme state (from Telegram)
  currentTheme: TelegramThemeParams | null;
  isThemeApplied: boolean;
  colorScheme: 'light' | 'dark' | null;

  // Loading and error state
  loading: boolean;
  error: string | null;

  // Recovery mode state
  isRecoveryMode: boolean;

  // Debug logs
  debugLog: string[];

  // Actions
  initialize: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<{ error: Error | null }>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
  updateEmail: (newEmail: string) => Promise<{ error: Error | null }>;
  refreshSession: () => Promise<{ error: Error | null }>;
  handleRecoveryFlow: () => Promise<void>;
  clearRecoveryMode: () => void;
  authenticateWithTelegram: () => Promise<void>;
  signInWithMagicLink: (email: string) => Promise<{ error: Error | null }>;
  setError: (error: string | null) => void;
  addLog: (msg: string) => void;
}

// Fallback: парсинг данных Telegram из URL hash, когда telegram-web-app.js не загрузился
// Hash содержит: tgWebAppData, tgWebAppThemeParams, tgWebAppVersion, tgWebAppPlatform и др.
function parseTgWebAppDataFromHash(): {
  initData: string;
  initDataUnsafe: Record<string, unknown>;
  themeParams: TelegramThemeParams | null;
  colorScheme: 'light' | 'dark';
} | null {
  const hash = window.location.hash;
  if (!hash.includes('tgWebAppData=')) return null;

  const hashParams = new URLSearchParams(hash.substring(1));
  const initData = hashParams.get('tgWebAppData');
  if (!initData) return null;

  // initData — это строка вида "query_id=X&user=%7B...%7D&auth_date=Y&hash=Z"
  const initParams = new URLSearchParams(initData);
  const initDataUnsafe: Record<string, unknown> = {};

  for (const [key, value] of initParams.entries()) {
    try {
      initDataUnsafe[key] = JSON.parse(value);
    } catch {
      initDataUnsafe[key] = value;
    }
  }

  // Парсим тему из tgWebAppThemeParams
  let themeParams: TelegramThemeParams | null = null;
  let colorScheme: 'light' | 'dark' = 'light';
  const themeParamsRaw = hashParams.get('tgWebAppThemeParams');
  if (themeParamsRaw) {
    try {
      themeParams = JSON.parse(themeParamsRaw);
      // Определяем светлая/тёмная тема по яркости bg_color
      if (themeParams?.bg_color) {
        const hex = themeParams.bg_color.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        colorScheme = luminance < 0.5 ? 'dark' : 'light';
      }
    } catch {
      // JSON parse failed — оставляем null
    }
  }

  return { initData, initDataUnsafe, themeParams, colorScheme };
}

export const useAuthStore = create<AuthState>((set, get) => ({
  // Initial state
  user: null,
  status: 'loading',
  authContext: 'web',
  isTelegram: false,
  telegramUser: null,
  isInitialized: false,
  isAuthenticating: false,
  currentTheme: null,
  isThemeApplied: false,
  colorScheme: null,
  loading: false,
  error: null,
  isRecoveryMode: false,
  debugLog: [],

  addLog: (msg: string) => {
    set((state) => ({ debugLog: [...state.debugLog, `${new Date().toISOString().split('T')[1]} ${msg}`] }));
  },

  // Initialize auth state
  initialize: async () => {
    const state = get();
    if (state.isInitialized) return;

    const { addLog } = get();
    addLog('Initialize: started');

    // Check if we're in Telegram context
    const telegramApiAvailable = isTelegramApiAvailable();
    const isTelegramApp = hasTelegramInitData();
    // Fallback: парсим tgWebAppData из URL hash (мобильный Telegram — SDK может не загрузиться)
    const tgHashData = !telegramApiAvailable ? parseTgWebAppDataFromHash() : null;
    // Восстановление Telegram-контекста после redirect
    const telegramRedirectPending = sessionStorage.getItem('v2d_telegram_auth');
    const effectiveTelegram = isTelegramApp || !!tgHashData || !!telegramRedirectPending;
    addLog(`Initialize: API=${telegramApiAvailable}, App=${isTelegramApp}, hashData=${!!tgHashData}, redirect=${!!telegramRedirectPending}`);

    if (telegramRedirectPending) {
      sessionStorage.removeItem('v2d_telegram_auth');
      addLog('Initialize: restoring Telegram context after redirect');
    }

    if (telegramApiAvailable || effectiveTelegram) {
      console.log('Initializing Telegram WebApp context');
      addLog('Initialize: initializing WebApp');

      // Initialize Telegram WebApp
      if (telegramApiAvailable) {
        initTelegramWebApp();
      }

      if (effectiveTelegram) {
        const telegramUser = tgHashData?.initDataUnsafe?.user || getTelegramUser();
        // Тема: SDK > hash fallback > null
        const themeParams = getTelegramThemeParams() || tgHashData?.themeParams || null;
        const scheme = (telegramApiAvailable ? getTelegramColorScheme() : null) || tgHashData?.colorScheme || 'light';

        // Apply theme if available
        if (themeParams) {
          applyTelegramTheme(themeParams);
        }

        set({
          isTelegram: true,
          telegramUser,
          authContext: 'telegram',
          currentTheme: themeParams,
          colorScheme: scheme,
          isThemeApplied: !!themeParams
        });
        addLog('Initialize: set Telegram state');

        // Set up theme change listener
        if (window.Telegram?.WebApp) {
          const handleThemeChange = () => {
            const newTheme = getTelegramThemeParams();
            const newScheme = getTelegramColorScheme();

            if (newTheme) {
              applyTelegramTheme(newTheme);
              set({
                currentTheme: newTheme,
                colorScheme: newScheme,
                isThemeApplied: true
              });
            }
          };

          window.Telegram.WebApp.onEvent('themeChanged', handleThemeChange);
        }
      } else {
        set({ authContext: 'web' });
        addLog('Initialize: Web context (in API)');
      }
    } else {
      set({ authContext: 'web' });
      addLog('Initialize: Web context (no API)');
    }

    try {
      // Set up auth state listener
      supabase.auth.onAuthStateChange(
        async (event, session) => {
          console.log('Auth state change:', event, session?.user?.id);
          // Only log significant events to avoid spam
          if (event !== 'INITIAL_SESSION') {
            get().addLog(`AuthState: change event=${event}`);
          }

          if (session?.user) {
            const user: User = {
              id: session.user.id,
              email: session.user.email || '',
              minutes: 30,
              createdAt: session.user.created_at,
            };

            // Сначала устанавливаем authenticated — НЕ блокируя UI
            set({
              user,
              status: 'authenticated',
              loading: false
            });

            // ВАЖНО: НЕ вызывать supabase.rpc() / supabase.auth.* напрямую
            // внутри onAuthStateChange callback — это вызывает deadlock auth lock.
            // supabase.rpc() -> _getAccessToken() -> getSession() -> _acquireLock()
            // но lock уже захвачен _emitInitialSession -> deadlock.
            // Все supabase.* вызовы выносим через setTimeout(0).

            // Инициализация и идентификация в OpenPanel (вне auth lock)
            if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN') {
              const sessionUser = session.user;
              setTimeout(async () => {
                try {
                  useOpenPanelStore.getState().init();

                  // Получить visitor_id через RPC (fallback-цепочка на стороне сервера)
                  let visitorId: string | null = null;
                  try {
                    const { data, error } = await supabase.rpc('get_my_visitor_id');
                    if (!error && data) visitorId = data;
                  } catch {
                    // RPC недоступен — fallback на user_id ниже
                  }

                  useOpenPanelStore.getState().identify({
                    profileId: visitorId || sessionUser.id,
                    email: sessionUser.email,
                  });

                  // Retry: при первой регистрации worker может не успеть записать openpanel_id
                  if (!visitorId || visitorId === sessionUser.id) {
                    setTimeout(async () => {
                      if (useAuthStore.getState().status !== 'authenticated') return;
                      try {
                        const { data, error } = await supabase.rpc('get_my_visitor_id');
                        if (!error && data && data !== sessionUser.id) {
                          useOpenPanelStore.getState().identify({
                            profileId: data,
                            email: sessionUser.email,
                          });
                        }
                      } catch {
                        // Retry не удался — оставляем текущий profileId
                      }
                    }, 3000);
                  }
                } catch (analyticsError) {
                  console.error('OpenPanel analytics error:', analyticsError);
                }
              }, 0);
            }

            // Handle recovery mode (вне auth lock)
            const urlParams = new URLSearchParams(window.location.search);
            const hashParams = new URLSearchParams(window.location.hash.substring(1));
            const type = urlParams.get('type') || hashParams.get('type');

            if (type === 'recovery') {
              console.log('Recovery mode detected');
              set({ isRecoveryMode: true });

              const accessToken = hashParams.get('access_token');
              const refreshToken = hashParams.get('refresh_token');

              if (accessToken && refreshToken) {
                // Вынос setSession из-под auth lock
                setTimeout(async () => {
                  try {
                    await supabase.auth.setSession({
                      access_token: accessToken,
                      refresh_token: refreshToken,
                    });
                  } catch (err) {
                    console.error('Error setting recovery session:', err);
                  }
                }, 0);
              }

              // Clean up URL
              const cleanUrl = window.location.pathname + window.location.search;
              window.history.replaceState({}, document.title, cleanUrl);

              // Redirect to reset-password page if needed
              if (window.location.pathname !== '/reset-password') {
                window.location.href = '/reset-password?type=recovery';
              }

              // Если мы в режиме восстановления, прерываем дальнейшую логику редиректов
              return;
            }

            // Handle redirect after login
            const returnTo = localStorage.getItem('returnTo');
            if (returnTo) {
              console.log('[Auth] Redirecting to saved path:', returnTo);
              localStorage.removeItem('returnTo');

              // Small delay to ensure auth state is fully propagated
              setTimeout(() => {
                window.location.href = returnTo;
              }, 100);

              return; // Don't process further if redirecting
            }
          } else {
            // Полный сброс OpenPanel при выходе (отключение трекинга)
            if (event === 'SIGNED_OUT') {
              useOpenPanelStore.getState().reset();
            }

            set({
              user: null,
              status: 'unauthenticated',
              loading: false
            });
          }
        }
      );

      // === ПЕРЕД getSession: обработать implicit-flow токены из hash ===
      const hashFragment = window.location.hash;
      if (hashFragment.includes('access_token')) {
        addLog('Initialize: found access_token in hash, setting session');
        const hashParams = new URLSearchParams(hashFragment.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');

        if (accessToken && refreshToken) {
          try {
            const { error: setSessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });
            // Очистить URL чтобы не обрабатывать повторно
            window.history.replaceState({}, document.title, window.location.pathname);
            if (setSessionError) {
              addLog(`Initialize: setSession error=${setSessionError.message}`);
            } else {
              addLog('Initialize: setSession from hash success');
            }
          } catch (e) {
            addLog(`Initialize: setSession exception=${String(e)}`);
          }
        }
      }

      // Check for existing session
      addLog('Initialize: checking session');
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) {
        throw error;
      }

      if (session?.user) {
        addLog('Initialize: session found');
        const user: User = {
          id: session.user.id,
          email: session.user.email || '',
          minutes: 30,
          createdAt: session.user.created_at,
        };
        set({
          user,
          status: 'authenticated',
          loading: false
        });
      } else {
        addLog('Initialize: no session');
        // Check for Supabase auth parameters in URL (hash or search)
        // This prevents infinite loops when returning from Magic Link
        const hash = window.location.hash;
        const search = window.location.search;
        addLog(`Initialize: URL check hash=${hash.substring(0, 50)}... search=${search.substring(0, 50)}...`);

        const hasAuthParams =
          hash.includes('access_token') ||
          hash.includes('type=recovery') ||
          hash.includes('error_description') ||
          search.includes('code='); // PKCE support

        addLog(`Initialize: hasAuthParams=${hasAuthParams}`);

        if (hasAuthParams) {
          console.log('Auth initialize: detected auth params in URL, waiting for session processing...');
          addLog('Initialize: waiting for params processing');

          // Try to manually handle the session if Supabase didn't catch it automatically
          // This often happens in Telegram WebApp environment
          const hashString = window.location.hash.startsWith('#')
            ? window.location.hash.substring(1)
            : window.location.hash;

          const hashParams = new URLSearchParams(hashString);
          const accessToken = hashParams.get('access_token');
          const refreshToken = hashParams.get('refresh_token');
          const errorDescription = hashParams.get('error_description');

          if (accessToken && refreshToken) {
            addLog('Initialize: found tokens in hash, manually setting session');
            try {
              const { error } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken,
              });

              if (error) {
                addLog(`Initialize: manual setSession error=${error.message}`);
                set({
                  status: 'unauthenticated',
                  loading: false,
                  error: translateAuthError(error.message)
                });
              } else {
                addLog('Initialize: manual setSession success');
                // onAuthStateChange will trigger update

                // Clean up URL to prevent reprocessing on refresh
                const cleanUrl = window.location.pathname + window.location.search;
                window.history.replaceState({}, document.title, cleanUrl);
              }
            } catch (e) {
              addLog(`Initialize: manual setSession exception=${String(e)}`);
              set({ status: 'unauthenticated', loading: false });
            }
          } else if (errorDescription) {
            addLog(`Initialize: auth error in URL=${errorDescription}`);
            set({
              status: 'unauthenticated',
              loading: false,
              error: translateAuthError(errorDescription)
            });
          } else {
            addLog('Initialize: hasAuthParams true but no tokens/error found (maybe code flow?)');

            if (window.location.search.includes('code=')) {
              addLog('Initialize: detected PKCE code, exchanging manually...');
              const searchParams = new URLSearchParams(window.location.search);
              const code = searchParams.get('code');
              if (code) {
                try {
                  const { data: exchangeData, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
                  if (exchangeError) {
                    addLog(`Initialize: PKCE exchange error=${exchangeError.message}`);
                    set({
                      status: 'unauthenticated',
                      loading: false,
                      error: translateAuthError(exchangeError.message)
                    });
                  } else {
                    addLog('Initialize: PKCE exchange success');
                    // onAuthStateChange will handle setting authenticated state
                    // Clean up URL
                    const cleanUrl = window.location.pathname;
                    window.history.replaceState({}, document.title, cleanUrl);
                  }
                } catch (e) {
                  addLog(`Initialize: PKCE exchange exception=${String(e)}`);
                  set({ status: 'unauthenticated', loading: false });
                }
              }
            }
          }
        } else {
          // If in Telegram context, try auto-login BEFORE setting unauthenticated
          if (effectiveTelegram) {
            console.log('Auth initialize: Telegram app detected, attempting auto-login...');
            addLog('Initialize: attempting Telegram auto-login');
            // Status remains 'loading' to prevent ProtectedRoute from redirecting to /login
            await get().authenticateWithTelegram();

            const currentStatus = get().status;
            if (currentStatus !== 'authenticated') {
              addLog('Initialize: auto-login failed, showing login');
              set({
                status: 'unauthenticated',
                loading: false
              });
            }
          } else {
            // Web context - go straight to login
            set({
              status: 'unauthenticated',
              loading: false
            });
            addLog('Initialize: Web context, showing login');
            console.log('Auth initialize: нет активной сессии, показываем веб-авторизацию');
          }
        }
      }
    } catch (error) {
      console.error('Не удалось получить сессию Supabase при инициализации:', error);
      addLog(`Initialize: error=${String(error)}`);
      set({
        user: null,
        status: 'unauthenticated',
        loading: false,
        error: error instanceof Error ? error.message : String(error)
      });
      console.log('Auth initialize: ошибка Supabase, переключаемся в веб-режим без сессии');
    } finally {
      set({ isInitialized: true });
      addLog('Initialize: finished');
    }
  },

  // Authenticate with Telegram
  authenticateWithTelegram: async () => {
    const state = get();
    const { addLog } = state;

    // Don't authenticate if already authenticated
    if (state.status === 'authenticated' && state.user) {
      console.log('User already authenticated, skipping Telegram auth');
      addLog('Auth: already authenticated');
      return;
    }

    // Получаем initData из SDK или из URL hash (fallback для мобильного Telegram)
    const sdkInitData = window.Telegram?.WebApp?.initData;
    const sdkInitDataUnsafe = window.Telegram?.WebApp?.initDataUnsafe;
    const hashFallback = !sdkInitData ? parseTgWebAppDataFromHash() : null;
    const initData = sdkInitData || hashFallback?.initData;
    const initDataUnsafe = sdkInitDataUnsafe || hashFallback?.initDataUnsafe;

    if (!initData) {
      console.log('Telegram: no initData from SDK or URL hash');
      addLog('Auth: no initData (SDK or hash)');
      return;
    }

    addLog(`Auth: initData source=${sdkInitData ? 'SDK' : 'hash'}, len=${initData.length}`);

    try {
      console.log('Starting Telegram authentication');
      addLog('Auth: starting');
      set({ isAuthenticating: true });

      const magicLinkUrl = import.meta.env.VITE_MAGIC_LINK_URL;
      if (!magicLinkUrl) {
        throw new Error('Magic link URL not configured');
      }

      const response = await fetch(magicLinkUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          initData,
          initDataUnsafe,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        addLog(`Auth: API error ${response.status}`);
        throw new Error(`Request error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      const { magic_link } = data;
      if (!magic_link) throw new Error('No magic_link in response');

      addLog(`Auth: got magic_link`);

      // === Попытка 1: verifyOtp (быстро, без перезагрузки, сохраняет initData) ===
      const linkUrl = new URL(magic_link);
      const tokenHash = linkUrl.searchParams.get('token_hash') || linkUrl.searchParams.get('token');
      addLog(`Auth: magic_link params=${Array.from(linkUrl.searchParams.keys()).join(',')}`);

      if (tokenHash) {
        addLog('Auth: trying verifyOtp...');
        try {
          const { data: verifyData, error: authError } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: 'magiclink',
          });

          if (!authError && verifyData?.session) {
            addLog('Auth: verifyOtp success');
            set({ isAuthenticating: false });
            return;
          }

          addLog(`Auth: verifyOtp failed: ${authError?.message || 'no session returned'}`);
        } catch (verifyErr) {
          addLog(`Auth: verifyOtp exception: ${String(verifyErr)}`);
        }
      }

      // === Попытка 2: redirect (тот же механизм что и web magic link) ===
      // Сохраняем Telegram-контекст в sessionStorage для восстановления после reload
      addLog('Auth: falling back to redirect');
      sessionStorage.setItem('v2d_telegram_auth', '1');
      window.location.href = magic_link;
      // Страница перезагрузится, initialize() обработает токены из URL
      // НЕ выходим из try/catch — await на window.location.href не нужен,
      // но код ниже не должен выполниться
      await new Promise(() => {}); // Блокируем выполнение до перезагрузки
    } catch (error) {
      console.error('Telegram authentication error:', error);
      addLog(`Auth: error=${String(error)}`);
      set({
        error: 'Не удалось выполнить вход через Telegram',
        isAuthenticating: false
      });
    }
  },

  signInWithMagicLink: async (email: string) => {
    set({ loading: true, error: null });
    try {
      const attributionKey = getStoredAttributionKey();
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/login`,
          shouldCreateUser: true,
          data: {
            registration_source: 'webapp',
            ...(attributionKey ? { attribution_key: attributionKey } : {}),
          },
        },
      });
      if (error) throw error;
      return { error: null };
    } catch (err) {
      const error = err as Error;
      const translatedMessage = translateAuthError(error.message);
      set({ error: translatedMessage });
      return { error: { ...error, message: translatedMessage } };
    } finally {
      set({ loading: false });
    }
  },

  // Web authentication actions
  login: async (email: string, password: string) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
    } catch (err) {
      const error = err as Error;
      set({ error: translateAuthError(error.message) });
    } finally {
      set({ loading: false });
    }
  },

  register: async (email: string, password: string) => {
    set({ loading: true, error: null });
    try {
      const attributionKey = getStoredAttributionKey();
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/login?confirmed=true`,
          data: {
            registration_source: 'webapp',
            ...(attributionKey ? { attribution_key: attributionKey } : {}),
          },
        },
      });
      if (error) throw error;
      return { error: null };
    } catch (err) {
      const error = err as Error;
      const translatedMessage = translateAuthError(error.message);
      set({ error: translatedMessage });
      return { error: { ...error, message: translatedMessage } };
    } finally {
      set({ loading: false });
    }
  },

  logout: async () => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      if (error) throw error;
    } catch (err) {
      const error = err as Error;
      const translatedMessage = translateAuthError(error.message);
      set({ error: translatedMessage });
      console.error('Logout error:', translatedMessage);
    } finally {
      set({
        user: null,
        status: 'unauthenticated',
        loading: false
      });
    }
  },

  resetPassword: async (email: string) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password?type=recovery`,
      });
      if (error) throw error;
      return { error: null };
    } catch (err) {
      const error = err as Error;
      const translatedMessage = translateAuthError(error.message);
      set({ error: translatedMessage });
      return { error: { ...error, message: translatedMessage } };
    } finally {
      set({ loading: false });
    }
  },

  updatePassword: async (newPassword: string) => {
    set({ loading: true, error: null });
    try {
      const state = get();

      // Skip session validation for recovery mode
      if (!state.isRecoveryMode) {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          const { error } = await supabase.auth.refreshSession();
          if (error) throw new Error('Сессия истекла. Пожалуйста, войдите снова.');
        }
      }

      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });
      if (error) throw error;
      return { error: null };
    } catch (err) {
      const error = err as Error;
      const translatedMessage = translateAuthError(error.message);
      set({ error: translatedMessage });
      return { error: { ...error, message: translatedMessage } };
    } finally {
      set({ loading: false });
    }
  },

  updateEmail: async (newEmail: string) => {
    set({ loading: true, error: null });
    try {
      // Ensure valid session before email update
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        const { error } = await supabase.auth.refreshSession();
        if (error) throw new Error('Сессия истекла. Пожалуйста, войдите снова.');
      }

      const { error } = await supabase.auth.updateUser(
        { email: newEmail },
        {
          emailRedirectTo: `${window.location.origin}/email-confirmation`,
        }
      );
      if (error) throw error;
      return { error: null };
    } catch (err) {
      const error = err as Error;
      const translatedMessage = translateAuthError(error.message);
      set({ error: translatedMessage });
      return { error: { ...error, message: translatedMessage } };
    } finally {
      set({ loading: false });
    }
  },

  refreshSession: async () => {
    try {
      const { data: { session }, error } = await supabase.auth.refreshSession();
      if (error) {
        console.error('Session refresh error:', error);
        return { error: new Error(translateAuthError(error.message)) };
      }

      if (session?.user) {
        const user: User = {
          id: session.user.id,
          email: session.user.email || '',
          minutes: 30,
          createdAt: session.user.created_at,
        };
        set({ user, status: 'authenticated' });
      }

      return { error: null };
    } catch (err) {
      const error = err as Error;
      console.error('Session refresh failed:', error);
      return { error: new Error(translateAuthError(error.message)) };
    }
  },

  handleRecoveryFlow: async () => {
    const state = get();

    if (state.isRecoveryMode) {
      console.log('Already in recovery mode');
      return;
    }

    // Check URL for recovery parameters
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const type = urlParams.get('type') || hashParams.get('type');

    if (type === 'recovery') {
      console.log('Recovery mode detected manually');

      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');

      if (accessToken && refreshToken) {
        try {
          await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
        } catch (err) {
          console.error('Error setting manual recovery session:', err);
        }
      }

      set({ isRecoveryMode: true });

      // Clean up URL
      const cleanUrl = window.location.pathname + window.location.search;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  },

  clearRecoveryMode: () => {
    console.log('Clearing recovery mode');
    set({ isRecoveryMode: false });

    // Clean URL from recovery parameters
    const cleanUrl = window.location.pathname + window.location.search;
    window.history.replaceState({}, document.title, cleanUrl);
  },

  setError: (error: string | null) => {
    set({ error });
  },
}));
